/**
 * Secure Logger Utility
 *
 * Replaces console.log with a development-only logger that:
 * - Only logs in development mode
 * - Sanitizes sensitive data
 * - Provides different log levels
 * - Can be disabled entirely for production
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogOptions {
  sanitize?: boolean;
  includeTimestamp?: boolean;
}

class Logger {
  private isDevelopment: boolean;
  private enabled: boolean;

  constructor() {
    // Vitest runs Vite in development mode too; emitting application telemetry
    // there floods otherwise successful test output and obscures real harness
    // warnings. Tests that assert logging mock this module explicitly.
    this.isDevelopment = import.meta.env.DEV && !import.meta.env.TEST;
    this.enabled = this.isDevelopment;
  }

  /**
   * Sanitize sensitive data from log output
   */
  private sanitize(data: any): any {
    if (typeof data !== "object" || data === null) {
      return data;
    }

    // Handle Error objects (including AxiosError) whose properties are non-enumerable
    // and would otherwise be lost when spread into a plain object.
    if (data instanceof Error) {
      const plain: Record<string, any> = {
        message: data.message,
        name: data.name,
      };
      // Axios-specific fields
      if ("response" in data) {
        const axiosErr = data as any;
        plain.status = axiosErr.response?.status;
        plain.data = axiosErr.response?.data;
        plain.code = axiosErr.code;
      }
      return this.sanitize(plain);
    }

    const sensitiveKeys = [
      "password",
      "token",
      "accessToken",
      "refreshToken",
      "apiKey",
      "secret",
      "authorization",
      "ssn",
      "creditCard",
    ];

    const sanitized = { ...data };

    for (const key in sanitized) {
      if (
        sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))
      ) {
        sanitized[key] = "[REDACTED]";
      } else if (
        typeof sanitized[key] === "object" &&
        sanitized[key] !== null
      ) {
        sanitized[key] = this.sanitize(sanitized[key]);
      }
    }

    return sanitized;
  }

  /**
   * Format log message with timestamp
   */
  private format(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    if (data !== undefined) {
      return `${prefix} ${message}`;
    }

    return `${prefix} ${message}`;
  }

  /**
   * Internal log method
   */
  private log(
    level: LogLevel,
    message: string,
    data?: any,
    options?: LogOptions,
  ) {
    if (!this.enabled || !this.isDevelopment) {
      return;
    }

    const shouldSanitize = options?.sanitize ?? true;
    const processedData = shouldSanitize && data ? this.sanitize(data) : data;
    const formattedMessage = options?.includeTimestamp
      ? this.format(level, message, processedData)
      : message;

    const consoleMethod = console[level] || console.log;

    if (processedData !== undefined) {
      consoleMethod(formattedMessage, processedData);
    } else {
      consoleMethod(formattedMessage);
    }
  }

  /**
   * Debug level logging
   */
  public debug(message: string, data?: any, options?: LogOptions) {
    this.log("debug", message, data, options);
  }

  /**
   * Info level logging
   */
  public info(message: string, data?: any, options?: LogOptions) {
    this.log("info", message, data, options);
  }

  /**
   * Warning level logging
   */
  public warn(message: string, data?: any, options?: LogOptions) {
    this.log("warn", message, data, options);
  }

  /**
   * Error level logging
   */
  public error(message: string, error?: Error | any, options?: LogOptions) {
    this.log("error", message, error, options);
  }

  /**
   * Enable or disable logging
   */
  public setEnabled(enabled: boolean) {
    this.enabled = enabled && this.isDevelopment;
  }

  /**
   * Group related logs together
   */
  public group(label: string) {
    if (this.enabled && this.isDevelopment) {
      console.group(label);
    }
  }

  /**
   * End a log group
   */
  public groupEnd() {
    if (this.enabled && this.isDevelopment) {
      console.groupEnd();
    }
  }

  /**
   * Log a table (useful for arrays of objects)
   */
  public table(data: any[], options?: LogOptions) {
    if (this.enabled && this.isDevelopment) {
      const shouldSanitize = options?.sanitize ?? true;
      const processedData = shouldSanitize ? this.sanitize(data) : data;
      console.table(processedData);
    }
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;

// Convenience exports for common use cases
export const logDebug = logger.debug.bind(logger);
export const logInfo = logger.info.bind(logger);
export const logWarn = logger.warn.bind(logger);
export const logError = logger.error.bind(logger);
